import React, { Fragment, useEffect, useState } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { showResult } from "../../utils/constants";

import {
  Grid,
  Typography,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormLabel,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import LazyLoad from "react-lazyload";
import CircularProgress from "@mui/material/CircularProgress";
import SearchField from "../shared/search-field";
import PageHeader from "../shared/page-header";

import {
  getUsersPackagesSelector,
  getUsersPackages,
  clearSubmission,
  getLocations,
  getEmailTemplates,
  getEmailTemplatesSelector,
  getFilteredLocationsSelector,
  clearUsersPackages,
  sendReminderEmail,
} from "../../store/admin";
import authService from "../../services/authService";
import UserPackages from "./user-packages";
import Loading from "../shared/loading/loading-view";

const ApplicationManagement = ({ ...props }) => {
  const { usersPackages, locations, emailTemplates } = props;
  const [open, setOpen] = useState(false);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState(2);
  const [selectedUserPackage, setSelectedUserPackage] = useState(null);
  const [showLoader, setShowLoader] = useState(false);
  const [syncResult, setSyncResult] = useState({});
  const [filter, setFilter] = useState({
    username: "",
    firstName: "",
    lastName: "",
    locationId: 0,
    show: "",
  });

  useEffect(() => {
    const init = async () => {
      const { clearSubmission, getLocations, getEmailTemplates } = props;
      const token = authService.getCurrentUser();
      setFilter({ ...filter, locationId: parseInt(token.groupsid) });
      await getLocations();
      await getEmailTemplates();
      clearSubmission();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (e, name) => {
    setFilter({ ...filter, [name]: e.target.value });
  };

  const doClear = async () => {
    const { clearUsersPackages } = props;
    const token = authService.getCurrentUser();

    const reset = {
      username: "",
      firstName: "",
      lastName: "",
      locationId: parseInt(token.groupsid),
      show: "",
      take: 100,
      skip: 0,
    };

    setFilter(reset);
    clearUsersPackages();
  };

  const doSearch = async () => {
    const { getUsersPackages, clearUsersPackages } = props;

    setShowLoader(true);
    await clearUsersPackages();

    let slice = 3000;
    let syncing = true;
    let skip = 0;
    let take = slice;

    while (syncing) {
      const result = await getUsersPackages({ ...filter, skip, take });
      setSyncResult({ ...result, skip });
      syncing = result.remainingRecords;
      skip = skip + slice;
    }

    setShowLoader(false);
    setSyncResult({});
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUserPackage(null);
  };

  const handleSendEmail = async () => {
    setOpen(false);
    const { sendReminderEmail } = props;
    const { user } = selectedUserPackage;

    const packageIds = selectedUserPackage.userPackages
      .filter((up) => up.isComplete === false)
      .map((up) => up.package.id);

    var userPackage = {
      user,
      packageIds,
      emailInfo: {
        sendEmail: true,
        emailTemplateId: selectedEmailTemplate,
      },
    };

    sendReminderEmail(userPackage);
    setSelectedUserPackage(null);
  };

  return (
    <Fragment>
      <PageHeader
        title="Applications"
        description="Patient applications are displayed below. To filter patients please fill in filters below."
      />
      {filter && (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={4}>
            <SearchField
              label="Search by username/email"
              value={filter.username}
              onChange={(e) => handleSearchChange(e, "username")}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SearchField
              label="Search by first name"
              value={filter.firstName}
              onChange={(e) => handleSearchChange(e, "firstName")}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SearchField
              label="Search by last name"
              value={filter.lastName}
              onChange={(e) => handleSearchChange(e, "lastName")}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="locationId-label">Location</InputLabel>
              <Select
                labelId="locationId-label"
                id="locationId"
                label="Location"
                value={filter.locationId}
                onChange={(e) => handleSearchChange(e, "locationId")}
              >
                {locations &&
                  locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <FormLabel sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                Show
              </FormLabel>
              <ToggleButtonGroup
                size="small"
                exclusive
                aria-label="show"
                value={filter.show || ""}
                onChange={(e, value) =>
                  handleSearchChange({ target: { value: value ?? "" } }, "show")
                }
                sx={{
                  "& .MuiToggleButton-root.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    "&:hover": { backgroundColor: "primary.dark" },
                  },
                }}
              >
                <ToggleButton value={showResult.completed}>Completed</ToggleButton>
                <ToggleButton value={showResult.notCompleted}>
                  Not Completed
                </ToggleButton>
                <ToggleButton value={showResult.exported}>Exported</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Grid>
        </Grid>
      )}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={doSearch}
          disabled={showLoader}
        >
          Search
        </Button>
        <Button variant="outlined" color="primary" onClick={doClear}>
          Clear
        </Button>
      </Stack>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          {showLoader && (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={2}
              sx={{ py: 6, width: "100%" }}
            >
              <CircularProgress color="primary" size={48} thickness={4} />
              <Typography variant="body1" color="text.secondary" align="center">
                {syncResult.total
                  ? `Searching records… ${syncResult.skip || 0} of ${syncResult.total}`
                  : "Searching records…"}
              </Typography>
            </Stack>
          )}
        </Grid>
        {usersPackages &&
          usersPackages.map((usersPackage) => (
            <LazyLoad key={usersPackage.user.id} placeholder={<Loading />}>
              <UserPackages
                key={usersPackage.user.id}
                usersPackage={usersPackage}
                setSelectedUserPackage={setSelectedUserPackage}
                setOpen={setOpen}
              />
            </LazyLoad>
          ))}
      </Grid>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Send Reminder"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Please select an email template to send to patient.
          </DialogContentText>
          <DialogContent>
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <InputLabel id="emailTemplateId-label">Email Templates</InputLabel>
              <Select
                labelId="emailTemplateId-label"
                id="emailTemplateId"
                label="Email Templates"
                value={selectedEmailTemplate}
                onChange={(e) => setSelectedEmailTemplate(e.target.value)}
              >
                {emailTemplates &&
                  emailTemplates.map((emailTemplate) => (
                    <MenuItem key={emailTemplate.id} value={emailTemplate.id}>
                      {emailTemplate.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </DialogContent>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleSendEmail} color="primary">
            Send Email
          </Button>
          <Button
            variant="contained"
            onClick={handleClose}
            color="primary"
            autoFocus
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    usersPackages: getUsersPackagesSelector(state),
    locations: getFilteredLocationsSelector(state),
    emailTemplates: getEmailTemplatesSelector(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    ...bindActionCreators(
      {
        getUsersPackages,
        clearSubmission,
        getLocations,
        getEmailTemplates,
        clearUsersPackages,
        sendReminderEmail,
      },
      dispatch
    ),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ApplicationManagement);
